import { motion } from 'framer-motion';
import { Star } from 'lucide-react';

type Review = {
  name: string;
  role: string;
  company: string;
  initials: string;
  hue: number;
  rating: number;
  quote: string;
};

const reviews: Review[] = [
  {
    name: 'Sarah Chen',
    role: 'VP Marketing',
    company: 'Helios DTC',
    initials: 'SC',
    hue: 210,
    rating: 5,
    quote:
      'SprynAI is the first AI tool that actually understands brand voice. Our weekly content output 3×’d without losing the polish.',
  },
  {
    name: 'David Okafor',
    role: 'Founder',
    company: 'Verge Studio',
    initials: 'DO',
    hue: 280,
    rating: 5,
    quote:
      'We retired four SaaS tools the week we onboarded. The reporting alone pays for itself — clients see results live.',
  },
  {
    name: 'Priya Raghavan',
    role: 'Head of Growth',
    company: 'Quartz Labs',
    initials: 'PR',
    hue: 330,
    rating: 5,
    quote:
      'Paid social CPA dropped 41% in six weeks. The AI bidding agent ships changes faster than any human ever could.',
  },
  {
    name: 'James Whitford',
    role: 'CMO',
    company: 'Atlas Resorts',
    initials: 'JW',
    hue: 30,
    rating: 5,
    quote:
      'A genuine unfair advantage. Our team plans Mondays, SprynAI executes the other six days. Bookings are up 2.2×.',
  },
  {
    name: 'Emma Larsen',
    role: 'Brand Director',
    company: 'Lumen',
    initials: 'EL',
    hue: 160,
    rating: 5,
    quote:
      'The brand guardrails are scary good. Every asset feels handcrafted — even the ones written at 3am by the AI.',
  },
  {
    name: 'Marcus Elliot',
    role: 'Head of Digital',
    company: 'Pioneer Co.',
    initials: 'ME',
    hue: 0,
    rating: 5,
    quote:
      'We grew the client roster from 12 to 31 in one year without growing headcount. SprynAI is the moat.',
  },
];

export default function Reviews() {
  return (
    <section
      id="reviews"
      className="relative bg-background text-foreground py-24 md:py-32 px-8 md:px-28"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-14"
        >
          <div className="liquid-glass inline-flex items-center gap-2 px-3 py-1.5 rounded-lg mb-6">
            <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase">
              Loved by operators
            </span>
          </div>
          <h2 className="text-4xl md:text-6xl font-medium tracking-[-1.5px] leading-[1.05]">
            What our partners{' '}
            <span className="font-serif italic font-normal">say about us.</span>
          </h2>
        </motion.div>

        <div className="columns-1 md:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {reviews.map((r, i) => (
            <motion.figure
              key={r.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.07 }}
              className="mb-5 break-inside-avoid rounded-2xl border border-border bg-card p-6"
            >
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: r.rating }).map((_, idx) => (
                  <Star
                    key={idx}
                    className="h-4 w-4 fill-foreground text-foreground"
                  />
                ))}
              </div>
              <blockquote className="text-base leading-7 text-foreground/90 mb-6">
                &ldquo;{r.quote}&rdquo;
              </blockquote>
              <figcaption className="flex items-center gap-3 pt-5 border-t border-border">
                <div
                  className="h-10 w-10 rounded-full flex items-center justify-center text-sm font-semibold text-white"
                  style={{
                    background: `linear-gradient(135deg, hsl(${r.hue} 70% 55%), hsl(${(r.hue + 40) % 360} 70% 40%))`,
                  }}
                >
                  {r.initials}
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    {r.name}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {r.role} · {r.company}
                  </div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
